import { liveQuery } from "dexie";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getGatewayBaseUrl } from "../runtime/config";
import { PosCustomerBenefitGatewayClient } from "../runtime/gateway/customer-benefit-client";
import { PosLocalProjectionGatewayClient } from "../runtime/gateway/local-projection-client";
import { PosSalesLayoutGatewayClient } from "../runtime/gateway/sales-layout-client";
import { PosStockActionGatewayClient } from "../runtime/gateway/stock-action-client";
import { PosStockTransferGatewayClient } from "../runtime/gateway/stock-transfer-client";
import { rebuildCatalogLookup } from "../runtime/local-store/catalog-lookup";
import { applyCommerceCalculationRuntime } from "../runtime/local-store/commerce-calculation-runtime";
import {
  applyCustomerBenefitRuntime,
  purgeCustomerBenefitsOutsideScope,
  readCustomerBenefitCursor,
} from "../runtime/local-store/customer-benefit-runtime";
import {
  applyCustomerCreditRuntime,
  applyEmployeeDirectoryRuntime,
  applyMemberRuntime,
  applyStockRuntime,
  purgeIndexedRuntimeOutsideScope,
  readLocalProjectionCursor,
} from "../runtime/local-store/indexed-runtime";
import { readLocalEmployeeSession, type LocalEmployeeSessionState } from "../runtime/local-store/employee-session";
import { applyLoyaltyRuntime } from "../runtime/local-store/loyalty-runtime";
import {
  markLocalProjectionSchemaCurrent,
  readVersionedLocalProjectionCursor,
} from "../runtime/local-store/local-projection-version";
import {
  applySalesLayoutRuntime,
  purgeSalesLayoutRuntimeOutsideScope,
  readLocalSalesLayoutRevision,
} from "../runtime/local-store/sales-layout-runtime";
import {
  applyStockActionRuntime,
  purgeStockActionRuntimeOutsideScope,
} from "../runtime/local-store/stock-action-runtime";
import {
  applyStockMovementRuntime,
  purgeStockMovementRuntimeOutsideScope,
} from "../runtime/local-store/stock-movement-runtime";
import {
  applyStockTransferRuntime,
  purgeStockTransferRuntimeOutsideScope,
} from "../runtime/local-store/stock-transfer-runtime";
import { readTerminalCredential, type TerminalCredentialState } from "../runtime/local-store/terminal-state";
import { useRuntimeAutoSync } from "../runtime/sync/use-runtime-auto-sync";
import { SalesLayoutCardPresentationRuntime } from "./SalesLayoutCardPresentationRuntime";

type Snapshot = { terminal: TerminalCredentialState | null; session: LocalEmployeeSessionState | null };

const STOCK_TRANSFER_FAST_SYNC_INTERVAL_MS = 3_000;

export function LocalProjectionRuntimeHost() {
  const [snapshot, setSnapshot] = useState<Snapshot>({ terminal: null, session: null });
  const [online, setOnline] = useState(navigator.onLine);
  const stockTransferSyncInFlightRef = useRef(false);

  useEffect(() => {
    const subscription = liveQuery(async () => ({
      terminal: await readTerminalCredential(),
      session: await readLocalEmployeeSession(),
    })).subscribe({ next: setSnapshot, error: () => setSnapshot({ terminal: null, session: null }) });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, []);

  const terminal = snapshot.terminal;
  const session = snapshot.session;
  const identity = terminal?.identity ?? null;
  const sameScope = Boolean(
    identity && session?.actor
    && session.actor.organization_id === identity.organization_id
    && session.actor.branch_id === identity.branch_id
    && session.actor.node_id === identity.node_id
    && session.actor.runtime_session_status === "ACTIVE",
  );
  const triggerKey = identity && sameScope
    ? `${identity.organization_id}:${identity.branch_id}:${identity.node_id}:${session?.actor.runtime_session_id ?? ""}`
    : "";
  const baseUrl = getGatewayBaseUrl();
  const client = useMemo(() => baseUrl ? new PosLocalProjectionGatewayClient({ baseUrl }) : null, [baseUrl]);
  const benefitClient = useMemo(() => baseUrl ? new PosCustomerBenefitGatewayClient({ baseUrl }) : null, [baseUrl]);
  const salesLayoutClient = useMemo(() => baseUrl ? new PosSalesLayoutGatewayClient({ baseUrl }) : null, [baseUrl]);
  const stockActionClient = useMemo(() => baseUrl ? new PosStockActionGatewayClient({ baseUrl }) : null, [baseUrl]);
  const stockTransferClient = useMemo(() => baseUrl ? new PosStockTransferGatewayClient({ baseUrl }) : null, [baseUrl]);

  useEffect(() => {
    if (!identity || !sameScope) return;
    void purgeIndexedRuntimeOutsideScope(identity);
    void purgeStockMovementRuntimeOutsideScope(identity);
    void purgeStockActionRuntimeOutsideScope(identity);
    void purgeStockTransferRuntimeOutsideScope(identity);
    void purgeCustomerBenefitsOutsideScope(identity);
    void purgeSalesLayoutRuntimeOutsideScope(identity);
    void rebuildCatalogLookup(identity);
  }, [identity?.organization_id, identity?.branch_id, identity?.node_id, sameScope]);

  const syncStockTransfer = useCallback(async () => {
    if (!stockTransferClient || !identity || !terminal?.deviceSecret || !session?.sessionToken || !online || !sameScope) return;
    if (stockTransferSyncInFlightRef.current) return;
    stockTransferSyncInFlightRef.current = true;
    try {
      const auth = { deviceSecret: terminal.deviceSecret, sessionToken: session.sessionToken };
      const stockTransfer = await stockTransferClient.loadRuntime({ ...auth, limit: 500 });
      if (stockTransfer.ok && stockTransfer.data) {
        await applyStockTransferRuntime(identity, stockTransfer.data);
      }
    } finally {
      stockTransferSyncInFlightRef.current = false;
    }
  }, [identity, online, sameScope, session?.sessionToken, stockTransferClient, terminal?.deviceSecret]);

  const sync = useCallback(async () => {
    if (!client || !benefitClient || !salesLayoutClient || !stockActionClient || !stockTransferClient || !identity || !terminal?.deviceSecret || !session?.sessionToken || !online || !sameScope) return;
    const auth = { deviceSecret: terminal.deviceSecret, sessionToken: session.sessionToken };

    // Catalog is already bootstrap/delta synced by its canonical runtime. Rebuild only the
    // dedicated hot lookup table from that durable local projection; no extra server read.
    await rebuildCatalogLookup(identity);

    // Sales Layout is a published configuration projection. Fetch by immutable runtime
    // revision in the background, then keep the effective document in IndexedDB. Product
    // rendering consumes only this local copy and never queries Core on click/render.
    const salesLayoutRevision = await readLocalSalesLayoutRevision(identity);
    const salesLayout = await salesLayoutClient.load({ ...auth, sinceRevision: salesLayoutRevision, limit: 50 });
    if (salesLayout.ok && salesLayout.data) {
      await applySalesLayoutRuntime(identity, salesLayout.data);
    }

    // Final-payable settings are a tiny organization-owned published projection. Refresh the
    // single active version in the background so cart/payment calculations remain local-first.
    const commerceCalculation = await client.load({ ...auth, domain: "COMMERCE_CALCULATION", cursor: null, limit: 1 });
    if (commerceCalculation.ok && commerceCalculation.data) {
      await applyCommerceCalculationRuntime(identity, commerceCalculation.data);
    }

    // Loyalty policy/reward catalog are published, cacheable runtimes. Cart interaction never
    // waits for this call; the latest successful snapshot remains the local evaluation input.
    const loyalty = await client.load({ ...auth, domain: "LOYALTY", cursor: null, limit: 1 });
    if (loyalty.ok && loyalty.data) {
      await applyLoyaltyRuntime(identity, loyalty.data);
    }

    // Stock action capability/reason metadata is an Owner projection, not client logic.
    // It is kept in the existing scoped projections store. Current v1 exposes ONLINE_REQUIRED
    // until a published offline authority proof/lease is available; item taps never fetch it.
    const stockActions = await stockActionClient.loadRuntime(auth);
    if (stockActions.ok && stockActions.data) {
      await applyStockActionRuntime(identity, stockActions.data);
    }

    // Transfer is an operational cross-branch queue. Reuse the dedicated lightweight
    // revalidation path so it can refresh independently of the much larger background bundle.
    await syncStockTransfer();

    let memberCursor = await readLocalProjectionCursor(identity, "MEMBER");
    for (let page = 0; page < 50; page += 1) {
      const result = await client.load({ ...auth, domain: "MEMBER", cursor: memberCursor, limit: 500 });
      if (!result.ok || !result.data) break;
      await applyMemberRuntime(identity, result.data);
      memberCursor = result.data.next_cursor;
      if (!result.data.has_more) break;
    }

    // Customer-specific Loyalty Reservations and Promotion/Web Claims are a projection only.
    // Checkout reads this local wallet; background sync is deliberately outside the sale path.
    let benefitCursor = await readCustomerBenefitCursor(identity);
    for (let page = 0; page < 50; page += 1) {
      const result = await benefitClient.load({ ...auth, cursor: benefitCursor, limit: 500 });
      if (!result.ok || !result.data) break;
      await applyCustomerBenefitRuntime(identity, result.data);
      benefitCursor = result.data.next_cursor;
      if (!result.data.has_more) break;
    }

    // Customer Credit payload gained derived outstanding/available-credit fields after the
    // first local projection release. Version-gate this rebuildable domain so existing POS
    // devices receive one FULL bootstrap instead of keeping a structurally stale payload
    // behind a valid delta cursor. Durable Journal/Outbox are not part of this reset.
    let creditCursor = await readVersionedLocalProjectionCursor(identity, "CUSTOMER_CREDIT");
    let creditProjectionComplete = false;
    for (let page = 0; page < 50; page += 1) {
      const result = await client.load({ ...auth, domain: "CUSTOMER_CREDIT", cursor: creditCursor, limit: 500 });
      if (!result.ok || !result.data) break;
      await applyCustomerCreditRuntime(identity, result.data);
      creditCursor = result.data.next_cursor;
      if (!result.data.has_more) {
        creditProjectionComplete = true;
        break;
      }
    }
    if (creditProjectionComplete) {
      await markLocalProjectionSchemaCurrent(identity, "CUSTOMER_CREDIT");
    }

    // Stock eligibility is branch-owned through the active branch SKU assignment. Version-gate
    // the rebuildable projection so terminals that previously cached the organization-wide item
    // universe receive one FULL snapshot and remove rows that are not stock-tracked here.
    let stockCursor = await readVersionedLocalProjectionCursor(identity, "STOCK");
    let stockProjectionComplete = false;
    for (let page = 0; page < 50; page += 1) {
      const result = await client.load({ ...auth, domain: "STOCK", cursor: stockCursor, limit: 500 });
      if (!result.ok || !result.data) break;
      await applyStockRuntime(identity, result.data);
      stockCursor = result.data.next_cursor;
      if (!result.data.has_more) {
        stockProjectionComplete = true;
        break;
      }
    }
    if (stockProjectionComplete) {
      await markLocalProjectionSchemaCurrent(identity, "STOCK");
    }

    // Movement history is a separate immutable transaction projection. It syncs in the
    // background and is never fetched from an item tap. Core already resolves branch scope,
    // permission, business label, actor presentation and authoritative post-balance.
    let stockHistoryCursor = await readVersionedLocalProjectionCursor(identity, "STOCK_HISTORY");
    let stockHistoryProjectionComplete = false;
    for (let page = 0; page < 50; page += 1) {
      const result = await client.load({ ...auth, domain: "STOCK_HISTORY", cursor: stockHistoryCursor, limit: 500 });
      if (!result.ok || !result.data) break;
      await applyStockMovementRuntime(identity, result.data);
      stockHistoryCursor = result.data.next_cursor;
      if (!result.data.has_more) {
        stockHistoryProjectionComplete = true;
        break;
      }
    }
    if (stockHistoryProjectionComplete) {
      await markLocalProjectionSchemaCurrent(identity, "STOCK_HISTORY");
    }

    let employeeCursor = await readLocalProjectionCursor(identity, "EMPLOYEE_DIRECTORY");
    for (let page = 0; page < 50; page += 1) {
      const result = await client.load({ ...auth, domain: "EMPLOYEE_DIRECTORY", cursor: employeeCursor, limit: 500 });
      if (!result.ok || !result.data) break;
      await applyEmployeeDirectoryRuntime(identity, result.data);
      employeeCursor = result.data.next_cursor;
      if (!result.data.has_more) break;
    }
  }, [benefitClient, client, identity, online, salesLayoutClient, sameScope, session?.sessionToken, stockActionClient, syncStockTransfer, terminal?.deviceSecret]);

  useRuntimeAutoSync({
    enabled: Boolean(triggerKey && online && stockTransferClient),
    sync: syncStockTransfer,
    triggerKey: triggerKey ? `${triggerKey}:stock-transfer-fast` : "",
    intervalMs: STOCK_TRANSFER_FAST_SYNC_INTERVAL_MS,
  });

  useRuntimeAutoSync({
    enabled: Boolean(triggerKey && online && client && benefitClient && salesLayoutClient && stockActionClient && stockTransferClient),
    sync,
    triggerKey,
  });

  return <SalesLayoutCardPresentationRuntime identity={identity && sameScope ? identity : null} />;
}
